import React from 'react';
import { Paper, Box, Typography, Chip } from '@mui/material';

/**
 * ExecutionCard Component
 *
 * Displays a single execution card with all its details.
 *
 * @param {Object} props
 * @param {Object} props.execution - Execution object
 * @param {number} props.index - Execution index (for display)
 */
function ExecutionCard({ execution, index }) {
  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={1}
      >
        <Typography variant="subtitle2" color="primary">
          Execution #{index + 1}
        </Typography>
        <Chip
          label={execution.success ? 'Success' : 'Failed'}
          size="small"
          color={execution.success ? 'success' : 'error'}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          ID: {execution.id}
        </Typography>
        {execution.template_version && (
          <Chip
            label={`v${execution.template_version}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Executed: {new Date(execution.created_at).toLocaleString()}
      </Typography>
      {execution.execution_time && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Duration: {execution.execution_time.toFixed(2)}s
        </Typography>
      )}
      {execution.model_used && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Model: {execution.model_used}
        </Typography>
      )}
      {execution.cost && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Cost: ${execution.cost.toFixed(4)}
        </Typography>
      )}
      {execution.token_usage &&
        Object.keys(execution.token_usage).length > 0 && (
          <Box mb={1}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Token Usage:
            </Typography>
            {Object.entries(execution.token_usage).map(([key, value]) => (
              <Chip
                key={key}
                label={`${key}: ${value}`}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>
        )}
      {execution.input_data && Object.keys(execution.input_data).length > 0 && (
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Input Data:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'grey.100',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
            }}
          >
            {JSON.stringify(execution.input_data, null, 2)}
          </Typography>
        </Box>
      )}
      {execution.formatted_prompt && (
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Formatted Prompt:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'grey.100',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            {execution.formatted_prompt}
          </Typography>
        </Box>
      )}
      {execution.raw_response && (
        <Box mb={1}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Response:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'grey.100',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            {execution.raw_response}
          </Typography>
        </Box>
      )}
      {execution.error_message && (
        <Box>
          <Typography
            variant="body2"
            color="error"
            gutterBottom
            fontWeight="bold"
          >
            {execution.error_code || 'Error'}:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              p: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              mb: 1,
            }}
          >
            {execution.error_message}
          </Typography>
          {execution.validation_errors &&
            execution.validation_errors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="error"
                  fontWeight="bold"
                  display="block"
                  gutterBottom
                >
                  Validation Errors ({execution.validation_errors.length}):
                </Typography>
                {execution.validation_errors.map((err, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    component="div"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                      p: 0.5,
                      borderRadius: 0.5,
                      fontSize: '0.7rem',
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    • {err.message || err.field || JSON.stringify(err)}
                  </Typography>
                ))}
              </Box>
            )}
        </Box>
      )}
    </Paper>
  );
}

export default ExecutionCard;
